import { addMinutes, startOfMinute } from "date-fns";
import { format } from "fecha";

import type { CreateFlatsParams } from "../../../api/flats/create";
import { assertInstanceOf } from "../../../utils/assertinstanceof";
import { InheritableEventEmitter } from "../../../utils/inheritableeventemitter";

/**
 * フラットファイル検索→検索条件エリア
 */
export class ConditionArea
    extends InheritableEventEmitter<{
        change: [params: Partial<CreateFlatsParams> | { invalid: string }];
    }> {

    /** 地震発生時刻（開始）の入力要素 */
    #jemOriginTimeFromElem: HTMLInputElement;
    /** 地震発生時刻（終了）の入力要素 */
    #jemOriginTimeToElem: HTMLInputElement;
    /** 気象庁マグニチュード（開始）の入力要素 */
    #mjmaFromElem: HTMLInputElement;
    /** 気象庁マグニチュード（終了）の入力要素 */
    #mjmaToElem: HTMLInputElement;
    /** 震源距離（開始）の入力要素 */
    #hypoDistFromElem: HTMLInputElement;
    /** 震源距離（終了）の入力要素 */
    #hypoDistToElem: HTMLInputElement;
    /** 加速度使用のチェックボックス要素 */
    #useAccToElem: HTMLInputElement;

    /**
     * コンストラクター。
     *
     * @param jemOriginTimeFromElem 地震発生時刻（開始）の入力要素
     * @param jemOriginTimeToElem 地震発生時刻（終了）の入力要素
     * @param mjmaFromElem 気象庁マグニチュード（開始）の入力要素
     * @param mjmaToElem 気象庁マグニチュード（終了）の入力要素
     * @param hypoDistFromElem 震源距離（開始）の入力要素
     * @param hypoDistToElem 震源距離（終了）の入力要素
     * @param useAccToElem 加速度使用のチェックボックス要素
     */
    constructor(
        jemOriginTimeFromElem: HTMLElement,
        jemOriginTimeToElem: HTMLElement,
        mjmaFromElem: HTMLElement,
        mjmaToElem: HTMLElement,
        hypoDistFromElem: HTMLElement,
        hypoDistToElem: HTMLElement,
        useAccToElem: HTMLElement) {

        super();

        assertInstanceOf(jemOriginTimeFromElem, HTMLInputElement);
        assertInstanceOf(jemOriginTimeToElem, HTMLInputElement);
        assertInstanceOf(mjmaFromElem, HTMLInputElement);
        assertInstanceOf(mjmaToElem, HTMLInputElement);
        assertInstanceOf(hypoDistFromElem, HTMLInputElement);
        assertInstanceOf(hypoDistToElem, HTMLInputElement);
        assertInstanceOf(useAccToElem, HTMLInputElement);

        this.#jemOriginTimeFromElem = jemOriginTimeFromElem;
        this.#jemOriginTimeToElem = jemOriginTimeToElem;
        this.#mjmaFromElem = mjmaFromElem;
        this.#mjmaToElem = mjmaToElem;
        this.#hypoDistFromElem = hypoDistFromElem;
        this.#hypoDistToElem = hypoDistToElem;
        this.#useAccToElem = useAccToElem;
    }

    /**
     * 初期化処理を行う。
     * 各入力要素にchangeイベントリスナーを設定する。
     */
    async init() {
        const jemOriginTimeFromElem = this.#jemOriginTimeFromElem;
        const jemOriginTimeToElem = this.#jemOriginTimeToElem;
        const mjmaFromElem = this.#mjmaFromElem;
        const mjmaToElem = this.#mjmaToElem;
        const hypoDistFromElem = this.#hypoDistFromElem;
        const hypoDistToElem = this.#hypoDistToElem;
        const useAccToElem = this.#useAccToElem;

        // oxlint-disable-next-line complexity
        const onChange = () => {
            if (!jemOriginTimeFromElem.reportValidity()) {
                this.emit("change", { invalid: "地震発生時刻の開始日時" });
                return;
            }

            if (!jemOriginTimeToElem.reportValidity()) {
                this.emit("change", { invalid: "地震発生時刻の終了日時" });
                return;
            }

            if (!mjmaFromElem.reportValidity()) {
                this.emit("change", { invalid: "気象庁マグニチュードの下限値" });
                return;
            }

            if (!mjmaToElem.reportValidity()) {
                this.emit("change", { invalid: "気象庁マグニチュードの上限値" });
                return;
            }

            if (!hypoDistFromElem.reportValidity()) {
                this.emit("change", { invalid: "震源距離の下限値" });
                return;
            }

            if (!hypoDistToElem.reportValidity()) {
                this.emit("change", { invalid: "震源距離の上限値" });
                return;
            }

            const jemOriginTimeFrom = (jemOriginTimeFromElem.value) ? jemOriginTimeFromElem.value + ":00" : undefined;
            const jemOriginTimeTo = (jemOriginTimeToElem.value) ? jemOriginTimeToElem.value + ":00" : undefined;
            const mjmaFrom = (mjmaFromElem.value) ? +mjmaFromElem.value : undefined;
            const mjmaTo = (mjmaToElem.value) ? +mjmaToElem.value : undefined;
            const hypoDistFrom = (hypoDistFromElem.value) ? +hypoDistFromElem.value : undefined;
            const hypoDistTo = (hypoDistToElem.value) ? +hypoDistToElem.value : undefined;
            const useAcc = (useAccToElem.checked) ? 1 : 0;

            const params = {
                jemOriginTimeFrom: (jemOriginTimeFrom) ?
                    format(startOfMinute(new Date(jemOriginTimeFrom)), "YYYY-MM-DDTHH:mm:ss") :
                    undefined,
                jemOriginTimeTo: (jemOriginTimeTo) ?
                    format(startOfMinute(addMinutes(new Date(jemOriginTimeTo), 1)), "YYYY-MM-DDTHH:mm:ss") :
                    undefined,
                mjmaFrom, mjmaTo,
                hypoDistFrom, hypoDistTo,
                useAcc,
            } satisfies Partial<CreateFlatsParams>;

            this.emit("change", params);
        };

        const elems = [
            jemOriginTimeFromElem,
            jemOriginTimeToElem,
            mjmaFromElem,
            mjmaToElem,
            hypoDistFromElem,
            hypoDistToElem,
            useAccToElem,
        ];

        for (const elem of elems) {
            elem.addEventListener("change", onChange);
            elem.addEventListener("input", onChange);
            elem.addEventListener("blur", onChange);
        }
    }
}
